import {
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from "firebase/auth";
import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import SaveButton from "../../components/SaveButton";
import SettingSection from "../../components/SettingSection";
import TextField from "../../components/styled/TextField";
import { DialogController } from "../../hooks/use-dialog";
import { pushError, pushSuccess } from "../../slices/notifications";
import { useDispatch } from "../../store";
import { parseErrorCode } from "../../utils/firebaseErrorParser";

export type PasswordResetFormParams = {
  actionCode: string;
};

type Props = {
  dialog: DialogController<PasswordResetFormParams>;
};

const PasswordResetForm: FC<Props> = ({ dialog }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [firstPassword, setFirstPassword] = useState<string>("");
  const [secondPassword, setSecondPassword] = useState<string>("");
  const auth = getAuth();

  useEffect(() => {
    if (!dialog.open) {
      setFirstPassword("");
      setSecondPassword("");
    }
  }, [dialog.open]);

  const close = () => {
    navigate("/login");
    dialog.handleClose();
  };
  const handleReset = async () => {
    verifyPasswordResetCode(auth, dialog?.data?.actionCode || "")
      .then(() => {
        confirmPasswordReset(
          auth,
          dialog?.data?.actionCode || "",
          firstPassword,
        )
          .then(() => {
            dispatch(pushSuccess("Successfully Updated Password!"));
            navigate("/login");
            dialog.handleClose();
          })
          .catch((error) => {
            dispatch(pushError(parseErrorCode(error)));
          });
      })
      .catch((error) => {
        dispatch(pushError(parseErrorCode(error)));
      });
  };

  return (
    <Dialog
      open={dialog.open}
      onClose={(_event, reason) => {
        if (reason && reason === "backdropClick") return;
        dialog.handleClose();
      }}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        Reset Password
        <IconButton
          color="primary"
          onClick={() => close()}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Container disableGutters>
          <Stack>
            <SettingSection label="Password" docsKey="">
              <TextField
                fullWidth
                name="new-password"
                type="password"
                autoComplete="no"
                value={firstPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFirstPassword(e.target.value)
                }
              />
            </SettingSection>
            <SettingSection label="Confirm Password" docsKey="">
              <TextField
                name="confirm-password"
                error={firstPassword !== secondPassword}
                fullWidth
                type="password"
                autoComplete="no"
                value={secondPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSecondPassword(e.target.value)
                }
              />
            </SettingSection>
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>Cancel</Button>
        <SaveButton
          color="primary"
          onClick={handleReset}
          disabled={!firstPassword || firstPassword !== secondPassword}
        >
          Request Password Reset
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetForm;
